import React, { Fragment, useEffect, useState } from 'react';
import { useTranslate, useNotify } from 'react-admin';
import { Add } from '@mui/icons-material';
import { Dialog, FormHelperText, DialogTitle, DialogContent, DialogActions, Grid, Skeleton, Typography, TextField, Button, Icon, Autocomplete, FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel } from '@mui/material';
import CurrencyInput from 'src/components/uiElements/fields/CurrencyInput'
import { billingFormServices } from 'src/services/payment/billingForm/billingForm.service';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Stack from '@mui/material/Stack';
import { reservationServices } from 'src/services/sales/reservation/reservation.service'


export const ModalCurrencyBase = ({ workUnitId, infoWorkUnit, missingBases, onClose, allCurrencies, onCloseWithoutvalidate }) => {
    const translate = useTranslate()
    const notify = useNotify()
    const [editingIndex, setEditingIndex] = useState(null);
    const [basesByWorkUnit, setBasesByWorkUnit] = useState([]);
    const [currenciesRelationsByWorkUnit, setCurrenciesRelationsByWorkUnit] = useState([]);
    const [isLoadingCurrencyBase, setIsLoadingCurrencyBases] = useState(false);
    const [currencyBaseRelations, setCurrencyBaseRelations] = useState([]);
    const [basesErrors, setBasesErrors] = useState({});
    const [currencyFromErrors, setCurrencyFromErrors] = useState({});
    const [currencyToErrors, setCurrencyToErrors] = useState({});
    const [inputValueErrors, setInputValueErrors] = useState({});
    const [isLoadingButtons, setIsLoadingButton] = useState(false)

    useEffect(() => {
        getCurrencyBases();
    }, []);

    const getCurrencyBases = async () => {
        setIsLoadingCurrencyBases(true);
        try {
            const defaultBase = infoWorkUnit?.billing_default_base || null;
            const alternateBaseOptions = infoWorkUnit?.billing_alternate_base?.options || [];

            let currencyBases = Array.from(
                new Set([defaultBase, ...alternateBaseOptions].filter(Boolean))
            ).map((name, index) => ({ id: index, name }));

            // Si `currencyBases` está vacío o es null, hacer la llamada adicional
            if (!currencyBases || currencyBases.length === 0) {
                const allBases = await reservationServices.getAllAlternativeBases();
                currencyBases = allBases.map((name, index) => ({ id: index, name }));
            }

            setBasesByWorkUnit(currencyBases);

            const filteredRelations = infoWorkUnit.currency_relation.filter(
                (relation) => relation.is_reverse === false
            );
            const updatedRelations = [...filteredRelations];

            missingBases.forEach(({ from, to, missingBases }) => {
                const fromCurrency = allCurrencies.find(currency => currency.short_name === from);
                const toCurrency = allCurrencies.find(currency => currency.short_name === to);

                if (fromCurrency && toCurrency) {
                    const exists = updatedRelations.some(
                        relation =>
                            relation.currency_relation.short_name === from &&
                            relation.currency_to_relation.short_name === to
                    );

                    if (!exists) {
                        updatedRelations.push({
                            work_unit_id: null, // Cambiar si necesitas un ID específico
                            updated_at: null,
                            is_reverse: false,
                            value: null, // Ajustar el valor si necesitas uno predeterminado
                            created_at: null,
                            currency_to_id: toCurrency.id,
                            currency_id: fromCurrency.id,
                            currency_relation: { ...fromCurrency },
                            currency_to_relation: { ...toCurrency },
                            bases: missingBases // Opcional, asociar las bases faltantes si es necesario
                        });
                    }
                }
            });
            setCurrenciesRelationsByWorkUnit(updatedRelations);
        } catch (error) {
            console.error("Error en getCurrencyBases:", error);
            setBasesByWorkUnit([]);
            setCurrenciesRelationsByWorkUnit([]);
        } finally {
            setIsLoadingCurrencyBases(false);
        }
    };

    const handleAddComponent = () => {
        setCurrencyBaseRelations((prevState) => [
            ...prevState,
            {
                id: null, // Indica que es un nuevo componente
                value: '',
                currency_from_relation: { id: '', short_name: '' },
                currency_to_relation: { id: '', short_name: '' },
                work_unit_hash: infoWorkUnit.hash_work_unit,
                bases: [],
                isEditing: true,
                is_inherited: false,
            },
        ]);
        setEditingIndex(currencyBaseRelations.length);
    };

    const handleInputValueChange = (index, value) => {
        setCurrencyBaseRelations((prevState) => {
            const newState = [...prevState];
            newState[index]['value'] = value;
            return newState;
        });
    };

    const handleBasesChange = (index, value) => {
        setCurrencyBaseRelations((prevState) => {
            const newState = [...prevState];
            newState[index].bases = value;
            return newState;
        });

        // Validar bases
        if (!value || value.length === 0) {
            setBasesErrors((prevErrors) => ({
                ...prevErrors,
                [index]: 'Selecciona al menos una base',
            }));
        } else {
            setBasesErrors((prevErrors) => {
                const newErrors = { ...prevErrors };
                delete newErrors[index];
                return newErrors;
            });
        }
    };

    const handleEditClick = (index) => {
        handleEdit(index);
    };

    const handleCurrencyFromChange = (index, value) => {
        const selectedCurrency = currenciesRelationsByWorkUnit.find(
            (currency) => currency.currency_relation.short_name === value
        );
        setCurrencyBaseRelations((prevState) => {
            const newState = [...prevState];
            newState[index].currency_from_relation = selectedCurrency
                ? { id: selectedCurrency.currency_relation.id, short_name: selectedCurrency.currency_relation.short_name }
                : { id: '', short_name: '' };
            if (newState[index].currency_to_relation.id === value) {
                newState[index].currency_to_relation = { id: '', short_name: '' };
            }
            return newState;
        });

        // Validar
        if (!value) {
            setCurrencyFromErrors((prevErrors) => ({
                ...prevErrors,
                [index]: 'Selecciona una moneda de origen',
            }));
        } else {
            setCurrencyFromErrors((prevErrors) => {
                const newErrors = { ...prevErrors };
                delete newErrors[index];
                return newErrors;
            });
        }
    };

    const handleCurrencyToChange = (index, value) => {
        const selectedCurrency = currenciesRelationsByWorkUnit.find(
            (currency) => currency.currency_to_relation.short_name === value
        );
        setCurrencyBaseRelations((prevState) => {
            const newState = [...prevState];
            newState[index].currency_to_relation = selectedCurrency
                ? { id: selectedCurrency.currency_to_relation.id, short_name: selectedCurrency.currency_to_relation.short_name }
                : { id: '', short_name: '' };
            return newState;
        });
    };

    const handleEdit = (index) => {
        setEditingIndex(index);
        setCurrencyBaseRelations((prevState) => {
            const newState = [...prevState];
            newState[index].isEditing = true;
            newState[index].is_inherited = false;
            return newState;
        });
    };

    const handleCancelEdit = async (index) => {
        if (currencyBaseRelations[index].id === null) {
            setCurrencyBaseRelations((prevState) => prevState.filter((_, i) => i !== index));
        }
        setEditingIndex(null);

        // Clear error messages for this index
        setCurrencyFromErrors((prevErrors) => {
            const newErrors = { ...prevErrors };
            delete newErrors[index];
            return newErrors;
        });

        setCurrencyToErrors((prevErrors) => {
            const newErrors = { ...prevErrors };
            delete newErrors[index];
            return newErrors;
        });

        setInputValueErrors((prevErrors) => {
            const newErrors = { ...prevErrors };
            delete newErrors[index];
            return newErrors;
        });

        setBasesErrors((prevErrors) => {
            const newErrors = { ...prevErrors };
            delete newErrors[index];
            return newErrors;
        });
    };

    const handleCheckboxChange = (index) => (event) => {
        setCurrencyBaseRelations((prevRelations) =>
            prevRelations.map((relation, i) =>
                i === index ? { ...relation, is_inherited: event.target.checked } : relation
            )
        );
    };

    const validateCurrencyBaseRelations = () => {
        const errors = [];

        // Combina todas las bases para cada relación única de currencyBaseRelations
        const combinedRelations = currencyBaseRelations.reduce((acc, relation) => {
            const key = `${relation.currency_from_relation.short_name}-${relation.currency_to_relation.short_name}`;
            if (!acc[key]) {
                acc[key] = { ...relation, bases: [...relation.bases] };
            } else {
                acc[key].bases.push(...relation.bases);
            }
            return acc;
        }, {});

        Object.entries(combinedRelations).forEach(([key, relation]) => {
            const baseCounts = relation.bases.reduce((acc, base) => {
                acc[base.name] = (acc[base.name] || 0) + 1;
                return acc;
            }, {});

            Object.entries(baseCounts).forEach(([baseName, count]) => {
                if (count > 1) {
                    errors.push(
                        `Base: "${baseName}" ${translate('ra.message.assignedMultipleTimesInRelationship')} ${relation.currency_from_relation.short_name} - ${relation.currency_to_relation.short_name}.`
                    );
                }
            });
        });

        missingBases.forEach(({ from, to, missingBases }) => {
            const relationKey = `${from}-${to}`;
            const relation = combinedRelations[relationKey];
            if (!relation) {
                errors.push(`${translate('ra.message.incompleteRelationshipBetween')} ${from} - ${to}.`);
                return;
            }
            const missingInRelation = missingBases.filter(
                (base) => !relation.bases.some((rBase) => rBase.name === base)
            );
            if (missingInRelation.length > 0) {
                errors.push(`${translate('ra.message.forTheRelationship')} ${from} - ${to}, ${translate('ra.message.missingFollowingBases')}: ${missingInRelation.join(', ')}.`);
            }
            if (!relation.value || relation.value.trim() === "") {
                errors.push(`${translate('ra.message.theRelationshipBetween')} ${from} - ${to} ${translate('ra.message.noAssignedValue')}.`);
            }
        });
        if (errors.length > 0) {
            notify(errors.join('\n'), { type: 'error' });
            return false;
        }
        return true;
    };

    const sendWithoutValidate = () =>{
        onCloseWithoutvalidate()
    }

    const handleSendData = async () => {
        setIsLoadingButton(true)
        let isValid = validateCurrencyBaseRelations()
        if (isValid) {
            const transformedData = currencyBaseRelations.map((currencyRelation) => ({
                currency_from: currencyRelation.currency_from_relation.short_name,
                currency_to: currencyRelation.currency_to_relation.short_name,
                value: typeof currencyRelation.value === 'number'
                    ? parseFloat(currencyRelation.value.toFixed(4))
                    : parseFloat(currencyRelation.value.replace(/\./g, '').replace(',', '.')),
                bases: currencyRelation.bases.map((base) => base.name),
                work_unit_hash: currencyRelation.work_unit_hash,
                is_inherited: false
            }));
            try {
                await billingFormServices.updateCurrencyBaseRelation(workUnitId, transformedData);
                notify(translate('ra.message.changesAppliedSuccessfully'), { type: 'success' });
                const saveButton = document.getElementById('saveButtonCustom'); if (saveButton) { saveButton.click(); }
                onClose()

            } catch (error) {
                notify('Error al aplicar cambios', { type: 'error' });
            }
            finally {
                setIsLoadingButton(false);
            }
        }
        setIsLoadingButton(false)
    }

    return (
        <>
            <DialogTitle> {translate('ra.message.attention')} </DialogTitle>
            <DialogContent dividers>
                <Grid item xs={12} md={12} lg={12} xl={12}>
                    <Stack sx={{ width: '100%' }} spacing={2} style={{ padding: '10px 0' }}>
                        <Alert severity="warning">
                            <AlertTitle>{translate('ra.message.createRelationsBetweenCurrenciesAndBases')}:</AlertTitle>
                            {missingBases && missingBases.length > 0 && (
                                missingBases.map((relation, index) => (
                                    <Fragment key={index}>
                                        <Typography variant="body2" gutterBottom>
                                            {`${translate('ra.message.forTheRelationship')} ${relation.from} - ${relation.to} ${translate('ra.message.missingValuesForBases')}: `}
                                            <Typography variant="body2" color="textSecondary" component="span">
                                                {relation.missingBases.join(', ')}
                                            </Typography>
                                        </Typography>
                                    </Fragment>
                                ))
                            )}
                        </Alert>
                    </Stack>

                    <Fragment>
                        {currencyBaseRelations?.map((currencyRelation, index) => (
                            <Grid container spacing={3} key={index} id={`containerCurrencyBaseRelation-${index}`} className="containerCurrencyBaseRelation">
                                {/* Botones de edición */}
                                <Grid item xs={12} container justifyContent="flex-end">
                                    {!currencyRelation.isEditing ? (
                                        <Button
                                            variant="text"
                                            size="small"
                                            startIcon={<Icon>edit</Icon>}
                                            onClick={() => handleEditClick(index)}
                                            style={{ color: editingIndex !== null && editingIndex !== index ? 'grey' : 'primary' }}
                                        >
                                            {translate('ra.action.edit')}
                                        </Button>
                                    ) : (
                                        <Button
                                            size="small"
                                            variant="text"
                                            startIcon={<Icon>cancel</Icon>}
                                            onClick={() => handleCancelEdit(index)}
                                            color="error"
                                        >
                                            {translate('ra.action.cancel')}
                                        </Button>
                                    )}
                                </Grid>

                                {/* Campos de Moneda Desde */}
                                <Grid item xs={12} md={4} lg={4} xl={4}>
                                    <FormControl fullWidth error={!!currencyFromErrors[index]}>
                                        <InputLabel id={`selectCurrencyFrom-${index}`}>{translate('ra.field.currency_from')} *</InputLabel>
                                        <Select
                                            labelId={`selectCurrencyFrom-${index}`}
                                            id={`selectCurrencyFrom-${index}`}
                                            value={currencyRelation.currency_from_relation.short_name || ''}
                                            label={translate('ra.field.currency_from')}
                                            onChange={(e) => handleCurrencyFromChange(index, e.target.value)}
                                            disabled={!currencyRelation.isEditing}
                                        >
                                            <MenuItem value="" disabled>
                                                <em>Seleccionar...</em>
                                            </MenuItem>
                                            {Array.from(new Set(currenciesRelationsByWorkUnit.map((currency) => currency.currency_relation.id)))
                                                .map((id) => {
                                                    const currency = currenciesRelationsByWorkUnit.find((item) => item.currency_relation.id === id);
                                                    return (
                                                        <MenuItem key={currency.currency_relation.id} value={currency.currency_relation.short_name}>
                                                            {currency.currency_relation.long_name}
                                                        </MenuItem>
                                                    );
                                                })}
                                        </Select>
                                        {currencyFromErrors[index] && (
                                            <FormHelperText>{currencyFromErrors[index]}</FormHelperText>
                                        )}
                                    </FormControl>
                                </Grid>

                                {/* Campos de Moneda Hasta */}
                                <Grid item xs={12} md={4} lg={4} xl={4}>
                                    <FormControl fullWidth error={!!currencyToErrors[index]}>
                                        <InputLabel id={`selectCurrencyTo-${index}`}>{translate('ra.field.currency_to')} *</InputLabel>
                                        <Select
                                            labelId={`selectCurrencyTo-${index}`}
                                            id={`selectCurrencyTo-${index}`}
                                            value={currencyRelation.currency_to_relation.short_name || ''}
                                            label={translate('ra.field.currency_to')}
                                            onChange={(e) => handleCurrencyToChange(index, e.target.value)}
                                            disabled={!currencyRelation.isEditing}
                                        >
                                            <MenuItem value="" disabled>
                                                <em>Seleccionar...</em>
                                            </MenuItem>
                                            {Array.from(new Set(currenciesRelationsByWorkUnit.map((currency) => currency.currency_to_relation.id)))
                                                .filter((id) => {
                                                    const currency = currenciesRelationsByWorkUnit.find((item) => item.currency_to_relation.id === id);
                                                    return currency && currency.currency_to_relation.short_name !== currencyRelation.currency_from_relation.short_name;
                                                })
                                                .map((id) => {
                                                    const currency = currenciesRelationsByWorkUnit.find((item) => item.currency_to_relation.id === id);
                                                    return (
                                                        <MenuItem key={currency.currency_to_relation.id} value={currency.currency_to_relation.short_name}>
                                                            {currency.currency_to_relation.long_name}
                                                        </MenuItem>
                                                    );
                                                })}
                                        </Select>
                                        {currencyToErrors[index] && (
                                            <FormHelperText>{currencyToErrors[index]}</FormHelperText>
                                        )}
                                    </FormControl>
                                </Grid>

                                {/* Campo de Input de Valor */}
                                <Grid item xs={12} md={4} lg={4} xl={4}>
                                    <FormControl fullWidth>
                                        <CurrencyInput
                                            fullWidth
                                            id={`inputValue-${index}`}
                                            label={`${translate('ra.field.value')} *`}
                                            placeholder="0,00"
                                            type="text"
                                            onChange={(e) => handleInputValueChange(index, e.target.value)}
                                            value={currencyRelation.value}
                                            disabled={!currencyRelation.isEditing}
                                            error={!!inputValueErrors[index]}
                                            helperText={inputValueErrors[index]}
                                            size="medium"
                                            maskOptions={{
                                                decimalLimit: 4, // Aquí defines los 4 decimales
                                            }}
                                        />
                                    </FormControl>
                                </Grid>

                                {/* Autocomplete para Bases */}
                                <Grid item xs={12} md={12} lg={12} xl={12}>
                                    <Autocomplete
                                        multiple
                                        id={`autocompleteBases-${index}`}
                                        options={basesByWorkUnit}
                                        getOptionLabel={(option) => option.name}
                                        value={currencyRelation.bases}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        filterSelectedOptions
                                        onChange={(event, value) => handleBasesChange(index, value)}
                                        disabled={!currencyRelation.isEditing}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Bases *"
                                                placeholder="Seleccionar bases..."
                                                error={!!basesErrors[index]}
                                                helperText={basesErrors[index]}
                                            />
                                        )}
                                    />
                                </Grid>

                                {/* Checkbox de Heredado */}
                                <Grid item xs={12} md={12} lg={12} xl={12}>
                                    {/* <FormControlLabel control={
                                        <Checkbox
                                            checked={!!currencyRelation?.is_inherited}
                                            onChange={handleCheckboxChange(index)}
                                            disabled
                                        />
                                    } label="Es heredado?" /> */}
                                    {translate('ra.field.isInherited')}: {!!currencyRelation?.is_inherited === true ? ' Si' : ' No'}
                                </Grid>
                            </Grid>
                        ))}

                        {/* Botón de agregar nueva relación */}
                        <Button variant="outlined" color="primary" startIcon={<Add />} onClick={handleAddComponent}>
                            {translate('ra.action.addNewRelation')}
                        </Button>
                    </Fragment>
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} color="primary">
                    {translate('ra.action.close')}
                </Button>
                <Button
                    onClick={sendWithoutValidate}
                    color="primary"
                    disabled={isLoadingButtons}>
                    {translate('ra.action.save_without_validate')}
                </Button>
                <Button
                    onClick={handleSendData}
                    color="primary"
                    disabled={isLoadingButtons}>
                    {translate('ra.action.save')}
                </Button>
            </DialogActions>
        </>
    )
}